import { Button, ButtonLevel, ButtonSize, ImpactBolt, Text, TextColor, TextVariant } from '@vizit/atoms';
import { Tooltip, TooltipTrigger } from '@vizit/atoms';
import { CheckIcon, InfoCircleIcon, RefreshCw04Icon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { MetricCardSkeleton } from '../metric-card-skeleton';
import { containerClasses, getWarningColor } from './styles';
import type { AssetOrderV2Props } from './types';

/**
 * Asset order metric card indicating whether assets are sorted
 * from highest to lowest score. Shows a warning "!" when unsorted
 * and a green checkmark when sorted correctly.
 *
 * @example
 * <AssetOrderV2 isSorted={false} onReorder={() => {}} />
 */
const AssetOrderV2 = React.forwardRef<HTMLDivElement, AssetOrderV2Props>((props, forwardedRef) => {
  const {
    isSorted,
    impactLevel = 'very-low',
    isDisabled = false,
    isFocused = false,
    isLoading = false,
    onReorder,
    className,
    ...other
  } = props;

  if (isLoading) {
    return (
      <div
        ref={forwardedRef}
        className={cx(containerClasses({ state: 'default' }), className)}
        aria-busy="true"
        data-testid="asset-order-v2"
        {...other}
      >
        <MetricCardSkeleton />
      </div>
    );
  }

  const state = isDisabled ? 'disabled' : isFocused ? 'focused' : 'default';

  return (
    <div
      ref={forwardedRef}
      className={cx(containerClasses({ state }), 'relative', className)}
      data-testid="asset-order-v2"
      data-state={state}
      {...other}
    >
      <div className="flex items-start justify-between w-full">
        <div className="flex items-center gap-1">
          <Text
            variant={TextVariant.MonoXs}
            color={TextColor.Quaternary}
            className={isDisabled ? 'text-fg-disabled-subtle' : ''}
            showEllipsis={false}
            data-testid="asset-order-name"
          >
            ASSET ORDER
          </Text>
          <TooltipTrigger delay={200}>
            <Button
              aria-label="Asset order info"
              level={ButtonLevel.Ghost}
              size={ButtonSize.Xs}
              className="cursor-default p-0 h-auto min-h-0"
            >
              <InfoCircleIcon size={12} className={isDisabled ? 'text-fg-disabled-subtle' : 'text-quaternary'} />
            </Button>
            <Tooltip>Arrange your assets high scoring to low to get this checkmark.</Tooltip>
          </TooltipTrigger>
        </div>
        <ImpactBolt level={impactLevel} hideUnfilled isDisabled={isDisabled} />
      </div>

      <div className="flex flex-col gap-2">
        {isSorted ? (
          <div className="flex items-center gap-2">
            <div
              data-testid="asset-order-success"
              className={cx(
                'flex items-center justify-center size-8',
                isDisabled ? 'bg-success-200' : 'bg-success-500',
              )}
            >
              <CheckIcon size={24} className="text-white" />
            </div>
          </div>
        ) : (
          <Text
            variant={TextVariant.MonoXl}
            elementType="span"
            color={TextColor.Inherit}
            className={getWarningColor(isDisabled)}
            showEllipsis={false}
            data-testid="asset-order-warning"
          >
            !
          </Text>
        )}
        <Text
          variant={TextVariant.MonoSm}
          color={TextColor.Tertiary}
          className={isDisabled ? 'text-fg-disabled-subtle' : ''}
          showEllipsis={false}
          data-testid="asset-order-text"
        >
          {isSorted ? 'Assets are in order!' : 'Sort high score to low'}
        </Text>
      </div>

      {onReorder && (
        <Button
          aria-label="Reorder assets"
          level={ButtonLevel.Secondary}
          size={ButtonSize.Xs}
          className="absolute bottom-3 right-3 !w-[28px] !h-[28px] !min-h-0 !p-1 !rounded-xs !gap-0"
          isDisabled={isDisabled || isSorted}
          onPress={onReorder}
        >
          <RefreshCw04Icon size={16} />
        </Button>
      )}
    </div>
  );
});

AssetOrderV2.displayName = 'AssetOrderV2';

export { AssetOrderV2 };
export type { AssetOrderV2Props };
